import { useCallback } from "react";
import { getLocalStorage, getLocalStorageJSON } from "../utils/storageCache";

const useDraftAndConflictHandlers = ({
  ws,
  files,
  selectedDiffLines,
  diffComments,
  setSelectedDiffLines,
  setDiffComments,
  sendSelectedContentToBackend,
  conflictDialog,
  setConflictDialog,
  setFiles,
  getLanguage,
  setActiveFile,
  addLocalMessage,
}) => {
  const handleConflictResolution = useCallback((choice, fileId) => {
    console.log("🔄 [冲突解决] 用户选择:", choice, "文件ID:", fileId);

    const conflict = conflictDialog;
    if (!conflict.isOpen || conflict.fileId !== fileId) {
      console.warn("冲突对话框状态不匹配，忽略操作");
      return;
    }

    if (choice === "adopt_new") {
      console.log("用户选择采用新文档C，B=C，A保持不变");
      setFiles((prevFiles) => ({
        ...prevFiles,
        [fileId]: {
          content: conflict.newContent,
          originalContent: conflict.originalContent,
          rawContent: conflict.originalContent,
          language: getLanguage(conflict.filename),
          meta: conflict.meta,
        },
      }));

      setActiveFile(fileId);

      if (addLocalMessage) {
        addLocalMessage(
          `📄 **已采用新文档：${conflict.filename}**\n\n` +
            `✅ 显示新文档C相对于原始版本A的差异\n` +
            `📋 新文档长度：${conflict.newContent.length} 字符\n` +
            `⚠️ 您之前的修改已丢失`
        );
      }
    } else if (choice === "keep_current") {
      console.log("用户选择保留当前编辑内容B，将A变量发送给后端");

      setFiles((prevFiles) => ({
        ...prevFiles,
        [fileId]: {
          content: conflict.currentContent,
          originalContent: conflict.originalContent,
          rawContent: conflict.originalContent,
          language: getLanguage(conflict.filename),
          meta: conflict.meta,
        },
      }));

      setActiveFile(fileId);

      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        const fileUpdateMessage = {
          type: "humaninput",
          input: `^^FILE_UPDATE:${conflict.originalContent}`,
          session_id: getLocalStorage("sessionId"),
          user_id: getLocalStorageJSON("userInfo", {})?.username,
        };
        ws.current.send(JSON.stringify(fileUpdateMessage));
        console.log("📤 [文件同步] 已发送A变量给后端，确保数据一致性");
      }

      if (addLocalMessage) {
        addLocalMessage(
          `📄 **已保留当前编辑：${conflict.filename}**\n\n` +
            `✅ 继续基于原始版本的修改\n` +
            `📋 当前内容长度：${conflict.currentContent.length} 字符\n` +
            `🔄 已同步原始版本到后端确保一致性`
        );
      }
    }

    setConflictDialog({
      isOpen: false,
      fileId: null,
      newContent: null,
      currentContent: null,
      originalContent: null,
      filename: null,
      meta: null,
    });
  }, [
    addLocalMessage,
    conflictDialog,
    getLanguage,
    setActiveFile,
    setConflictDialog,
    setFiles,
    ws,
  ]);

  const handleSendDraftToBackend = useCallback((fileId, fileName) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      const fileContent = files[fileId]?.content;
      if (!fileContent) {
        console.error("无法找到文件内容:", fileId);
        return;
      }

      if (!window._fileContents) {
        window._fileContents = {};
      }

      console.log("缓存文件内容 - fileId:", fileId, "- fileName:", fileName);

      const simpleFileName = fileName.split("/").pop();

      if (typeof fileContent === "object" && fileContent !== null) {
        const contentStr = JSON.stringify(fileContent);
        window._fileContents[fileId] = contentStr;
        window._fileContents[fileName] = contentStr;
        window._fileContents[simpleFileName] = contentStr;
      } else {
        window._fileContents[fileId] = fileContent;
        window._fileContents[fileName] = fileContent;
        window._fileContents[simpleFileName] = fileContent;
      }

      const selectedLines = selectedDiffLines[fileId] || [];
      const comments = diffComments[fileId] || {};

      sendSelectedContentToBackend(ws.current, fileId, selectedLines, comments);

      setSelectedDiffLines((prev) => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });

      setDiffComments((prev) => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });

      console.log(`已发送Draft文件 ${fileName} 的选中内容到后端`);
    } else {
      console.error("WebSocket未连接，无法发送Draft数据");
    }
  }, [
    diffComments,
    files,
    selectedDiffLines,
    sendSelectedContentToBackend,
    setDiffComments,
    setSelectedDiffLines,
    ws,
  ]);

  return {
    handleConflictResolution,
    handleSendDraftToBackend,
  };
};

export default useDraftAndConflictHandlers;
